import type { UseQueryResult } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import type { DateRange } from "react-day-picker";

import { DateRangePicker, ErrorContainer, FormInput, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Table } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaLock, FaRegEdit } from "react-icons/fa";

import type { GigClaim } from "@/api/queries";

import type { StatusOption } from "../utils/gigsFilterUtils";

import GigsPill from "../../../components/GigsPill";
import LoadingSmall from "../../../components/LoadingSmall";
import useGetColours from "../../../helpers/getColours";
import { GIGS_STATUS, STATUS_OPTIONS } from "../utils/gigsClaimConstants";
import { isStatusOption, matchesStatus } from "../utils/gigsFilterUtils";

type StatusIconCellProps = {
  gigStatus: number | undefined;
  highlightColour: string;
  adminRights: boolean;
};

function StatusIconCell({ gigStatus, highlightColour, adminRights }: StatusIconCellProps) {
  return (
    <div className="text-2xl gig-claim-icon">
      {gigStatus === GIGS_STATUS.NOT_VERIFIED && adminRights ? (
        <FaRegEdit style={{ color: highlightColour }} className="gig-claim-editable-pen-icon" />
      ) : (
        <FaLock className="text-old-sentric-submenu-icon" />
      )}
    </div>
  );
}

type AllGigsProps = {
  gigClaims: UseQueryResult<GigClaim[], Error>;
  adminRights: boolean;
};

function AllGigs({ gigClaims, adminRights }: AllGigsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { highlightColour } = useGetColours();

  const [venue, setVenue] = useState("");
  const [status, setStatus] = useState<StatusOption>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const handleClear = () => {
    setVenue("");
    setStatus("all");
    setDateRange(undefined);
  };

  const columns = useMemo((): ColumnDef<GigClaim>[] => [
    { accessorKey: "venueName", header: String(t("dashboard:ra-column-venue", "Venue")), size: 300 },
    {
      accessorKey: "date",
      header: String(t("dashboard:ra-column-date", "Date")),
      size: 120,
      cell: ({ row }) => {
        const date = row.original.date;
        return date !== "" && date !== "0000-00-00" ? date.split("T")[0] : "Unknown date";
      },
    },
    { accessorKey: "time", header: String(t("gigs:table-header-time", "Time")), size: 120 },
    { accessorKey: "setlistName", header: String(t("gigs:table-header-setlist", "Setlist")), size: 140 },
    {
      accessorKey: "gigStatus",
      id: "statusPill",
      header: String(t("songs:song-table-status", "Status")),
      size: 160,
      cell: ({ row }) => <GigsPill gigStatus={row.original.gigStatus ?? null} />,
    },
    {
      id: "actions",
      enableSorting: false,
      size: 60,
      cell: ({ row }) => (
        <StatusIconCell
          gigStatus={row.original.gigStatus}
          highlightColour={highlightColour}
          adminRights={adminRights}
        />
      ),
    },
  ], [t, highlightColour, adminRights]);

  const filteredGigs = useMemo(() => {
    const gigs = gigClaims.data ?? [];

    const venueTerm = venue.trim().toLowerCase();
    const fromStr = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
    const toStr = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

    return gigs.filter((gig) => {
      if (venueTerm !== "" && !gig.venueName?.toLowerCase().includes(venueTerm)) {
        return false;
      }

      if (!matchesStatus(gig.gigStatus, status)) {
        return false;
      }

      if (fromStr !== undefined || toStr !== undefined) {
        const gigDate = (gig.date !== "" && gig.date !== "0000-00-00")
          ? gig.date.split("T")[0]
          : null;
        if (gigDate === null)
          return false;
        if (fromStr !== undefined && gigDate < fromStr)
          return false;
        if (toStr !== undefined && gigDate > toStr)
          return false;
      }

      return true;
    });
  }, [gigClaims.data, venue, status, dateRange]);

  const onRowClick = (row: GigClaim) => {
    if (row.gigStatus === 1 || adminRights) {
      void navigate({ to: `/gigs/claim/update/${row.id}` });
    }
  };

  const filterContent = (
    <div className="col-span-full flex flex-col gap-4 md:flex-row">
      <FormInput
        smallFont
        label={t("gigs:filter-by-venue", "Venue")}
        placeholder={`${t("common:filter", "Filter")} ${t("common:gigs", "gigs")}`}
        value={venue}
        onChange={e => setVenue(e.target.value)}
      />
      <Select value={status} onValueChange={v => setStatus(isStatusOption(v) ? v : "all")}>
        <SelectTrigger smallFont label={t("gigs:filter-by-status", "Status")} className="pl-0 w-full">
          <SelectValue placeholder={t("gigs:filter-status-all", "All")} />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {t(opt.labelKey, opt.labelDefault)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DateRangePicker
        smallFont
        label={t("gigs:filter-by-date-range", "Date range")}
        initialRange={dateRange}
        onSelect={setDateRange}
        maxDate={new Date()}
        numberOfMonths={1}
      />
    </div>
  );

  const totalGigs = gigClaims.data?.length ?? 0;
  const filteredCount = filteredGigs.length;

  const statsBlock = useMemo(() => [
    {
      title: t("gigs:stat-total-gigs", "Total gigs"),
      value: totalGigs,
      highlight: false,
    },
    {
      title: t("gigs:stat-filtered-gigs", "Filtered"),
      value: filteredCount,
      highlight: true,
    },
  ], [totalGigs, filteredCount, t]);

  return (
    <div className="overflow-x-auto">
      {gigClaims.isLoading && <LoadingSmall />}
      {gigClaims.isSuccess && (
        <Table
          enableColumnResizing={false}
          data={filteredGigs}
          columns={columns}
          filterContent={filterContent}
          onRowClick={onRowClick}
          isRowClickable={(row: GigClaim) => row.gigStatus === GIGS_STATUS.NOT_VERIFIED || adminRights}
          onClear={handleClear}
          statsBlock={statsBlock}
        />
      )}
      {gigClaims.isError && (
        <ErrorContainer message={gigClaims.error?.message ?? t("common:errorGettingRecentGigs", "Error getting recent gigs")} />
      )}
    </div>
  );
}

export default AllGigs;
